import type { AiMessage, AiMessageFile } from '@/types/ai';

type ApiAiMessageFile = Partial<AiMessageFile> & {
  url?: string;
  filename?: string;
  type?: string;
};

type ApiAiMessage = Omit<AiMessage, 'files'> & {
  files?: ApiAiMessageFile[] | null;
};

function normalizeFile(file: ApiAiMessageFile): AiMessageFile | null {
  const fileUrl = file.file_url ?? file.url;
  if (typeof fileUrl !== 'string' || fileUrl.trim() === '') {
    return null;
  }

  const fileName = file.file_name ?? file.filename ?? 'document';
  const fileType = file.file_type ?? file.type ?? 'file';

  return {
    file_url: fileUrl,
    file_name: typeof fileName === 'string' && fileName.trim() !== '' ? fileName : 'document',
    file_type: typeof fileType === 'string' && fileType.trim() !== '' ? fileType : 'file',
  };
}

/**
 * Marker that the voice assistant appends after its injected system prompt and
 * right before the user's actual transcript (see voiceContextPrompts.ts).
 */
const VOICE_TRANSCRIPT_MARKER = "Voici ce que l'utilisateur a dit :";

/**
 * Signatures that reveal a message body is an injected system/voice prompt rather
 * than the words the shop owner actually said.
 */
const SYSTEM_PROMPT_SIGNATURES = [
  'MODE VOCAL ACTIF',
  'REGLES VOCALES',
  'ASSISTANT VOCAL',
  'assistant vocal',
  'MODE VENTE ASSISTEE',
  'VENTE ASSISTEE',
];

const TITLE_MAX_LENGTH = 64;

/** Removes leading bracketed tags, e.g. "[ASSISTANT VOCAL - CREATION DE PRODUIT] ". */
function stripLeadingTags(text: string): string {
  return text.replace(/^\s*(?:\[[^\]]*\]\s*)+/g, '');
}

/**
 * Returns the user's real words, stripped of any injected voice/system prompt
 * scaffolding. Returns '' when the message is purely an (unrecoverable) injected
 * prompt — callers should then show a neutral placeholder.
 */
export function sanitizeUserMessageContent(content: string | null | undefined): string {
  if (typeof content !== 'string') return '';

  const markerIndex = content.lastIndexOf(VOICE_TRANSCRIPT_MARKER);
  if (markerIndex !== -1) {
    return content.slice(markerIndex + VOICE_TRANSCRIPT_MARKER.length).trim();
  }

  const stripped = stripLeadingTags(content).trim();
  if (SYSTEM_PROMPT_SIGNATURES.some((signature) => stripped.includes(signature))) {
    return '';
  }
  return stripped;
}

/** Friendly fallback label inferred from an unrecoverable voice/system prompt. */
function inferVoiceTitle(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.includes('creer des produits') || lower.includes('creation de produit')) {
    return 'Création produit · vocal';
  }
  if (lower.includes('categor')) return 'Catégories · vocal';
  if (lower.includes('resume des ventes')) return 'Résumé des ventes · vocal';
  if (lower.includes('stock')) return 'Stock · vocal';
  if (lower.includes('vente')) return 'Vente assistée · vocal';
  return 'Conversation vocale';
}

/**
 * Produces a clean, human-readable conversation title. Normal text conversations
 * pass through (trimmed/truncated); voice conversations that leaked a system
 * prompt get the transcript — or a friendly label when unrecoverable.
 * Returns null when there is nothing usable (caller falls back to a default).
 */
export function cleanConversationTitle(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;

  const sanitized = sanitizeUserMessageContent(raw);
  if (sanitized) {
    const oneLine = sanitized.replace(/\s+/g, ' ').trim();
    return oneLine.length > TITLE_MAX_LENGTH
      ? `${oneLine.slice(0, TITLE_MAX_LENGTH - 1).trimEnd()}…`
      : oneLine;
  }

  return inferVoiceTitle(raw);
}

export function normalizeAiMessage(message: ApiAiMessage): AiMessage {
  const normalizedFiles = Array.isArray(message.files)
    ? message.files.map(normalizeFile).filter((file): file is AiMessageFile => file !== null)
    : null;

  const content =
    message.role === 'user' ? sanitizeUserMessageContent(message.content) : message.content;

  return {
    ...message,
    content,
    files: normalizedFiles,
  };
}
